import { FacilityStatus } from "encamp-prisma/generated/browser";

/** Format a date (Date or ISO string) for NNR reason text (e.g. "Closed as of 12/10/2024"). */
function formatReasonDate(date: Date | string): string {
  const parsed = typeof date === "string" ? new Date(date) : date;
  return parsed.toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });
}

export type FormattedNotReportingReasonOptions = {
  reportingYear?: number;
};

/**
 * Minimal facility shape for deriving a not-reporting reason. `status` is a string so callers
 * holding EITHER the Prisma `FacilityStatus` enum (backend) OR the Oak GraphQL `FacilityStatus`
 * enum (frontend) can pass it directly — both share the same string values. Dates accept a
 * `Date` (backend Prisma) or an ISO `string` (Oak GraphQL).
 */
export type NotReportingReasonFacility = {
  status?: string | null;
  isNotReporting?: boolean | null;
  isNotReportingThroughEncamp?: boolean | null;
  colocationReportingFacilityId?: string | null;
  dateClosed?: Date | string | null;
  dateInactive?: Date | string | null;
};

/**
 * Returns the full sentence reason for newly-not-reporting emails (e.g. "Closed as of 12/10/2024"
 * or "Closed for the entire reporting year"). Matches the copy in the NNR cover letter reasons block.
 * Use when building TemplateFacility.newlyNotReportingReason.
 */
export function getFormattedOakNotReportingReason(
  facility?: NotReportingReasonFacility,
  options?: FormattedNotReportingReasonOptions
): string | null {
  if (!facility) return null;

  const reportingYear = options?.reportingYear;
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";

  if (facility.status === FacilityStatus.CLOSED) {
    if (facility.dateClosed) {
      return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
    }
    return `Closed${fallbackSuffix}`.trim() || "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located with another reporting facility on the same site";
  }

  if (facility.isNotReporting) {
    return "Chemical inventory was below reporting thresholds for the entire reporting year";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    if (facility.dateInactive) {
      return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
    }
    return `Inactive${fallbackSuffix}`.trim() || "Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "No longer reporting through Encamp";
  }

  return null;
}

export function getOakNotReportingReason(facility?: NotReportingReasonFacility) {
  if (!facility) return null;

  if (facility.status === FacilityStatus.CLOSED) {
    return "Closed";
  }

  if (facility.colocationReportingFacilityId) {
    return "Co-located";
  }

  if (facility.isNotReporting) {
    return "Under Thresholds";
  }

  if (facility.status === FacilityStatus.INACTIVE) {
    return "Is Inactive";
  }

  if (facility.isNotReportingThroughEncamp) {
    return "Without Encamp";
  }

  return null;
}

/**
 * Short display label for a STORED `notReportingReason`. Fall back to
 * `getOakNotReportingReason(facility)` when null (historical, un-backfilled reports).
 * EXEMPT has no facility-derived equivalent, so it only surfaces here.
 */
export function formatStoredNotReportingReason(reason?: string | null): string | null {
  switch (reason) {
    case "CLOSED":
      return "Closed";
    case "COLOCATED":
      return "Co-located";
    case "UNDER_THRESHOLD":
      return "Under Thresholds";
    case "INACTIVE":
      return "Is Inactive";
    case "NOT_WITH_ENCAMP":
      return "Without Encamp";
    case "EXEMPT":
      return "Exempt";
    default:
      return null;
  }
}

/**
 * Full-sentence version of a STORED not-reporting reason. For CLOSED/INACTIVE this mirrors
 * the date-aware copy of `getFormattedOakNotReportingReason` (e.g. "Closed as of 12/10/2024"
 * / "Closed for the entire reporting year") using the same `facility.dateClosed` /
 * `facility.dateInactive` and `reportingYear` inputs, so preferring the stored reason loses
 * no regulatory wording. EXEMPT has no facility field to derive from; its note is appended
 * when present.
 */
export function getFormattedStoredNotReportingReason(
  reason?: string | null,
  note?: string | null,
  facility?: NotReportingReasonFacility,
  reportingYear?: number
): string | null {
  const fallbackSuffix = reportingYear != null ? " for the entire reporting year" : "";
  switch (reason) {
    case "CLOSED":
      if (facility?.dateClosed) {
        return `Closed as of ${formatReasonDate(facility.dateClosed)}`;
      }
      return `Closed${fallbackSuffix}`.trim() || "Closed";
    case "COLOCATED":
      return "Co-located with another reporting facility on the same site";
    case "UNDER_THRESHOLD":
      return "Chemical inventory was below reporting thresholds for the entire reporting year";
    case "INACTIVE":
      if (facility?.dateInactive) {
        return `Inactive as of ${formatReasonDate(facility.dateInactive)}`;
      }
      return `Inactive${fallbackSuffix}`.trim() || "Inactive";
    case "NOT_WITH_ENCAMP":
      return "No longer reporting through Encamp";
    case "EXEMPT":
      return note?.trim()
        ? `Exempt from Tier II reporting requirements: ${note.trim()}`
        : "Exempt from Tier II reporting requirements";
    default:
      return null;
  }
}

/**
 * Full-sentence not-reporting reason. Prefers the STORED reason (the explicit per-year source
 * of truth, and the only source for EXEMPT), falling back to deriving from live facility fields
 * only for historical / un-backfilled reports with no stored reason. Because the stored
 * formatter is now date-aware (it takes the same `facility` dates + `reportingYear`), preferring
 * the stored reason keeps the "Closed as of <date>" regulatory copy. Single source of truth
 * shared by the comms dataloaders and the report Not Reporting page.
 */
export function getResolvedNotReportingReasonSentence(args: {
  storedReason?: string | null;
  storedNote?: string | null;
  facility?: NotReportingReasonFacility;
  reportingYear?: number;
}): string | null {
  // `storedReason` is the Prisma enum value as a string here (not the GraphQL enum), so the
  // stored formatter's literal comparisons are intentional — this shared helper does not
  // import GraphQL types.
  return (
    getFormattedStoredNotReportingReason(
      args.storedReason,
      args.storedNote,
      args.facility,
      args.reportingYear
    ) ?? getFormattedOakNotReportingReason(args.facility, { reportingYear: args.reportingYear })
  );
}
